import React from "react";
import type {Theme} from "@mui/material";
import {Box, useTheme} from "@mui/material";
import type {EChartsType} from "echarts/types/dist/shared";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {useStores} from "~/stores";
import {MithraEchart} from "~/services/echarts/MithraEchart";
import type {EChartContextType} from "~/services/echarts/EChartProvider";
import {toCurrency} from "~/components/currency-component/CurrencyClasses";
import type {TaxonomySpend} from "~/prototypes/pages/taxonomy-canvas/mockTaxonomySpend.ts";
import {spendColor, spendTextColor} from "~/prototypes/pages/taxonomy-canvas/spendColor.ts";

const SERIES_ID = 'taxonomy';

type TreemapNode = {
    id: string
    name: string
    value: number
    itemStyle: {color: string, borderColor?: string}
    label: {color: string}
    upperLabel: {color: string}
    children?: TreemapNode[]
}

/** The (edited) taxonomy as ECharts treemap data; size = mock spend, colour = spend vs. the same level */
function toTreemapData(tree: m_taxonomy.Tree, spend: TaxonomySpend, theme: Theme): TreemapNode[] {
    const toNode = (n: m_taxonomy.Tree, depth: number): TreemapNode => {
        const value = spend.spendById.get(n.id) ?? 0;
        const share = spend.maxByDepth[depth] ? value / spend.maxByDepth[depth] : 0;
        const textColor = spendTextColor(theme, share);
        const color = spendColor(theme, share);
        const children = n.children.filter(c => !c.removed).map(c => toNode(c, depth + 1));
        return {
            id: String(n.id),
            name: n.label,
            value,
            // A parent's header strip is drawn in its border colour: use its own spend colour there too
            itemStyle: children.length > 0 ? {color, borderColor: color} : {color},
            label: {color: textColor},
            upperLabel: {color: textColor},
            ...(children.length > 0 ? {children} : {}),
        };
    };
    return tree.children.filter(c => !c.removed).map(c => toNode(c, 1));
}

type ChartData = {nodes: TreemapNode[], focusId?: number}

/** Drill into the chosen category (it becomes the treemap's root, with breadcrumbs back up); none = whole taxonomy */
function zoomTo(chart: EChartsType, focusId: number | undefined) {
    chart.dispatchAction({
        type: 'treemapRootToNode',
        seriesId: SERIES_ID,
        ...(focusId === undefined ? {} : {targetNodeId: String(focusId)}),
    });
}

type Props = {
    tree: m_taxonomy.Tree
    name: string
    spend: TaxonomySpend
    /** Category chosen in the level filters: the treemap zooms into it */
    focusId?: number
    height: number
}
/**
 * PROTOTYPE: the taxonomy as an ECharts treemap (via MithraEchart). Two levels at a time; click a block to
 * drill down, use the breadcrumb at the bottom to go back up.
 */
export const TaxonomyTreemap: React.FC<Props> = ({tree, name, spend, focusId, height}) => {
    const theme = useTheme();
    const {p} = useStores();
    const data: ChartData[] = React.useMemo(
        () => [{nodes: toTreemapData(tree, spend, theme), focusId}],
        [tree, spend, theme, focusId]
    );
    const format = (v: number) => toCurrency(p.currencySymbol, v, 3);

    const buildOption = (nodes: TreemapNode[]) => ({
        tooltip: {
            formatter: (info: {treePathInfo: {name: string}[], value: number}) => {
                // treePathInfo starts with the series root (the taxonomy itself): leave it out
                const path = info.treePathInfo.slice(1).map(p => p.name);
                const last = path.pop() ?? '';
                return `<div style="font-size:11px;opacity:.7">${path.join(' › ')}</div>`
                    + `<b>${last}</b><br/>${format(info.value)} (mock)`;
            },
        },
        series: [{
            id: SERIES_ID,
            type: 'treemap',
            name,
            data: nodes,
            top: 8,
            left: 8,
            right: 8,
            bottom: 40,
            roam: false,
            nodeClick: 'zoomToNode',
            leafDepth: 2,
            visibleMin: 200,
            label: {show: true, formatter: '{b}', fontSize: 12, overflow: 'truncate'},
            upperLabel: {show: true, height: 24, fontSize: 12, fontWeight: 600},
            itemStyle: {borderColor: theme.palette.common.white, borderWidth: 1, gapWidth: 2},
            levels: [
                {itemStyle: {borderWidth: 0, gapWidth: 4}},
                {itemStyle: {borderWidth: 3, gapWidth: 2}},
                {itemStyle: {borderColor: theme.palette.common.white, borderWidth: 2, gapWidth: 1}},
            ],
            breadcrumb: {
                show: true,
                bottom: 8,
                left: 8,
                itemStyle: {color: theme.palette.mithraBlue[800], borderColor: theme.palette.common.white},
                textStyle: {color: theme.palette.common.white},
            },
        }],
    });

    return <Box sx={{height, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper'}}>
        <MithraEchart
            data={data}
            initChart={(echart: EChartContextType, root: HTMLElement, d: ChartData[]) => {
                const chart = echart.init(root, 'mithra', {renderer: 'svg', height});
                chart.setOption(buildOption(d[0].nodes));
                zoomTo(chart, d[0].focusId);
                return chart;
            }}
            updateChart={(chart, d: ChartData[]) => {
                chart.setOption({series: [{id: SERIES_ID, data: d[0].nodes}]});
                zoomTo(chart, d[0].focusId);
            }}
        />
    </Box>
}
